import { Stack, Typography, useTheme } from '@mui/material';
import { useCallback, useEffect } from 'react';
import { useWatch } from 'react-hook-form';

import { Card } from '@/components/layout/Card/Card';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { FormModal } from '@/components/modals/FormModal/FormModal';
import { FormModalActions } from '@/components/modals/FormModal/FormModalActions';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { LoadingOverlay } from '@/components/progress/LoadingOverlay/LoadingOverlay';
import {
  FormProvider,
  useForm,
  useFormContext,
} from '@/components/react-hook-form';
import { shouldRefetchQuery } from '@/graphql/client.utils';
import { useFormSaveHandler } from '@/hooks/useFormSaveHandler';
import { useReportError } from '@/hooks/useReportError';

import { setNewHypotheticalItemNotification } from '../../EstateWaterfall.utils';
import {
  EstateWaterfallHypotheticalGratModalContextProvider,
  useEstateWaterfallHypotheticalGratModalContext,
} from './EstateWaterfallHypotheticalGratModal.context';
import { HypotheticalGratFormShape } from './EstateWaterfallHypotheticalGratModal.types';
import {
  getButtonText,
  getDefaultValues,
  getModalHeading,
  mapFormDataToCreateInput,
  mapFormDataToUpdateInput,
} from './EstateWaterfallHypotheticalGratModal.utils';
import {
  FundingRadioGroupField,
  SelectedAssetDetailsField,
  SimulatedGratSelectField,
  SourceEntitySelectField,
  TaxDiscountCard,
  TransferNameField,
} from './Form/HypotheticalGratForm.fields';
import {
  CreateEstateWaterfallHypotheticalGratMutation,
  EstateWaterfallHypotheticalGratModalDocument,
  useCreateEstateWaterfallHypotheticalGratMutation,
  useDeleteEstateWaterfallHypotheticalGratsMutation,
  useEstateWaterfallHypotheticalGratModalQuery,
  useUpdateEstateWaterfallHypotheticalGratMutation,
} from './graphql/EstateWaterfallHypotheticalGratModal.generated';
import { useSyncDataToForm } from './hooks/useSyncDataToForm';

export interface EstateWaterfallHypotheticalGratModalProps {
  isOpen: boolean;
  waterfallId: string;
  // gratId is null if we are creating a new hypothetical GRAT
  gratId: string | null;
  onClose: () => void;
  initialSourceId?: string;
  householdId: string;
}

function EstateWaterfallHypotheticalGratModalSyncer({
  householdId,
  waterfallId,
}: {
  householdId: string;
  waterfallId: string;
}) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const { syncDataToForm } = useSyncDataToForm();

  const { setIsLoading: setIsContextLoading } =
    useEstateWaterfallHypotheticalGratModalContext();

  const { data, loading } = useEstateWaterfallHypotheticalGratModalQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      waterfallId,
      householdId,
    },
    onError: (error) => {
      showFeedback('Failed to load hypothetical GRAT');
      reportError('Failed to load hypothetical GRAT', error);
    },
  });

  useEffect(() => {
    setIsContextLoading(loading);
  }, [loading, setIsContextLoading]);

  useEffect(() => {
    if (data) {
      syncDataToForm(data);
    }
  }, [data, syncDataToForm]);

  return null;
}

function EstateWaterfallHypotheticalGratModalInner({
  isOpen,
  onClose: onCloseExternal,
  waterfallId,
  gratId,
  householdId,
}: EstateWaterfallHypotheticalGratModalProps) {
  const theme = useTheme();
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();

  const { formRef, handleSave } = useFormSaveHandler();

  const {
    isLoading,
    gratOptions,
    sourceEntityOptions,
    sourceIndividualOptions,
    sourceValues,
    fundingId,
  } = useEstateWaterfallHypotheticalGratModalContext();

  const { control, handleSubmit, formState, shouldBlockNavigation } =
    useFormContext<HypotheticalGratFormShape>();

  const fundingKind = useWatch({
    control,
    name: 'fundingKind',
  });

  const handleClose = useCallback(() => {
    onCloseExternal();
  }, [onCloseExternal]);

  const [updateHypotheticalGrat] =
    useUpdateEstateWaterfallHypotheticalGratMutation();
  const [createHypotheticalGrat] =
    useCreateEstateWaterfallHypotheticalGratMutation();
  const [deleteHypotheticalGrats, { loading: deleteLoading }] =
    useDeleteEstateWaterfallHypotheticalGratsMutation({
      onCompleted: () => {
        showFeedback('Hypothetical GRAT deleted', {
          variant: 'success',
        });
        handleClose();
      },
      onError: (error) => {
        showFeedback('Failed to delete hypothetical GRAT');
        reportError('Failed to delete hypothetical GRAT', error);
      },
    });

  const onValidSubmit = (data: HypotheticalGratFormShape) => {
    if (gratId) {
      if (!gratId) {
        throw new Error('gratId is required');
      }

      return updateHypotheticalGrat({
        refetchQueries: 'active',
        onQueryUpdated: (query) => {
          return shouldRefetchQuery(query.queryName, {
            ignoredQueryDocuments: [
              EstateWaterfallHypotheticalGratModalDocument,
            ],
          });
        },
        variables: {
          input: mapFormDataToUpdateInput(data, gratId, fundingId, householdId),
        },
        onCompleted: () => {
          showFeedback(`Hypothetical GRAT updated`, {
            variant: 'success',
          });
          handleClose();
        },
        onError: (error) => {
          showFeedback(`Failed to update hypothetical GRAT`);
          reportError(`Failed to update hypothetical GRAT`, error);
        },
      });
    }

    return createHypotheticalGrat({
      refetchQueries: 'active',
      onQueryUpdated: (query) => {
        return shouldRefetchQuery(query.queryName, {
          ignoredQueryDocuments: [EstateWaterfallHypotheticalGratModalDocument],
        });
      },
      variables: {
        input: mapFormDataToCreateInput(data, waterfallId, householdId),
      },
      onCompleted: (
        response: CreateEstateWaterfallHypotheticalGratMutation
      ) => {
        showFeedback('Hypothetical GRAT created', {
          variant: 'success',
        });

        // Set the notification status for the newly created hypothetical GRAT
        if (response.createHypotheticalGrat?.id) {
          setNewHypotheticalItemNotification({
            entityId: response.createHypotheticalGrat.simulatedGrat.id,
            dismissed: false,
          });
        }

        handleClose();
      },
      onError: (error) => {
        showFeedback('Failed to create hypothetical GRAT');
        reportError('Failed to create hypothetical GRAT', error);
      },
    });
  };

  const onSubmit = handleSubmit(onValidSubmit);
  const isCreating = !gratId;

  return (
    <FormModal
      isOpen={isOpen}
      onClose={handleClose}
      heading={getModalHeading({ isCreating })}
      rightHeaderContent={null}
      shouldBlockClose={shouldBlockNavigation}
      actions={
        <FormModalActions.Provider formState={formState}>
          {!isCreating && (
            <FormModalActions.DeleteButton
              loading={deleteLoading}
              onConfirmDelete={() => {
                void deleteHypotheticalGrats({
                  variables: {
                    hgratIds: [gratId],
                  },
                });
              }}
            />
          )}
          <FormModalActions.CancelButton onClick={handleClose} />
          <FormModalActions.SaveButton onClick={handleSave}>
            {getButtonText({ isCreating })}
          </FormModalActions.SaveButton>
        </FormModalActions.Provider>
      }
    >
      <form ref={formRef} onSubmit={onSubmit} noValidate>
        <LoadingOverlay open={isLoading} updatingText="Loading..." />
        <FormLayoutRow columns={6}>
          <FormLayoutItem>
            <TransferNameField />
          </FormLayoutItem>
        </FormLayoutRow>
        <FormLayoutRow>
          <FormLayoutItem>
            <SimulatedGratSelectField
              householdId={householdId}
              gratOptions={gratOptions}
            />
          </FormLayoutItem>
        </FormLayoutRow>
        <Stack spacing={2}>
          <Card
            variant="filled"
            sx={{ p: 2, pb: 4, borderRadius: theme.spacing(0.75) }}
          >
            <Stack>
              <Typography variant="title-l" sx={{ pb: 2 }}>
                Transfer details
              </Typography>
              <FormLayoutRow>
                <FormLayoutItem>
                  <SourceEntitySelectField
                    sourceEntityOptions={sourceEntityOptions}
                    sourceIndividualOptions={sourceIndividualOptions}
                  />
                </FormLayoutItem>
              </FormLayoutRow>
              <FormLayoutRow>
                <FormLayoutItem>
                  <FundingRadioGroupField sourceValues={sourceValues} />
                </FormLayoutItem>
              </FormLayoutRow>
              {fundingKind === 'assets' && (
                <FormLayoutRow>
                  <FormLayoutItem>
                    <SelectedAssetDetailsField sourceValues={sourceValues} />
                  </FormLayoutItem>
                </FormLayoutRow>
              )}
            </Stack>
          </Card>
          <TaxDiscountCard sourceValues={sourceValues} />
        </Stack>
      </form>
    </FormModal>
  );
}

export function EstateWaterfallHypotheticalGratModal(
  props: EstateWaterfallHypotheticalGratModalProps
) {
  const formMethods = useForm<HypotheticalGratFormShape>({
    defaultValues: getDefaultValues({
      sourceId: props.initialSourceId,
    }),
  });

  return (
    <FormProvider {...formMethods}>
      <EstateWaterfallHypotheticalGratModalContextProvider
        waterfallId={props.waterfallId}
        gratId={props.gratId}
        initialSourceId={props.initialSourceId}
      >
        <EstateWaterfallHypotheticalGratModalSyncer
          householdId={props.householdId}
          waterfallId={props.waterfallId}
        />
        <EstateWaterfallHypotheticalGratModalInner {...props} />
      </EstateWaterfallHypotheticalGratModalContextProvider>
    </FormProvider>
  );
}
